import { useState } from "react";
import { OPERACIA_LAMINOVANIE } from "@/lib/operacie";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useOperacie } from "@/api/hooks/useOperacie";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { formatEur } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  KATEGORIE_OPERACII,
  OPERACIA_ZAOBLENIE,
  zoskupOperacie,
} from "@/lib/operacie";
import type { Dielec, Operation } from "@/api/schemas";
import { bezDiakritiky } from "@/lib/hladanie";

/**
 * Výber platenej operácie (drážka, striekanie…) → vloží sa ako VNORENÝ
 * riadok dielca (parent_dielec_id, bez materiálu). Množstvo počíta
 * backend z rozmerov rodiča (m² = X×Y, m = X, ks = 1) × počet ks riadku.
 */
export function PridatOperaciuDialog({
  zakazkaId,
  dielec,
  open,
  onOpenChange,
}: {
  zakazkaId: string;
  dielec: Dielec;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const createDielec = useCreateDielec(zakazkaId, dielec.zostava_id);
  const operacieQuery = useOperacie({ systemove: false });
  // Zaoblenie masívu účtuje appka sama podľa strán (KOLO 168) — ako
  // vnorená operácia by sa zaplatilo dvakrát
  // Zaoblenie a laminovanie (KOLO 234) pridáva appka sama — laminovanie
  // bez laminátu by bola len práca bez listu
  const operacie = (operacieQuery.data?.data ?? []).filter(
    (o) => o.kod !== OPERACIA_ZAOBLENIE && o.kod !== OPERACIA_LAMINOVANIE,
  );
  const [q, setQ] = useState("");
  const [kat, setKat] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const filtered = operacie.filter(
    (o) =>
      (!kat || o.kategoria === kat) &&
      (!q ||
        bezDiakritiky(`${o.kod} ${o.label ?? ""} ${o.popis ?? ""}`).includes(bezDiakritiky(q))),
  );
  const skupiny = zoskupOperacie(filtered);
  // chips len pre kategórie, ktoré reálne majú operácie
  const dostupneKategorie = KATEGORIE_OPERACII.filter((k) =>
    operacie.some((o) => o.kategoria === k.kod),
  );

  async function pick(op: Operation): Promise<void> {
    setBusy(true);
    try {
      await createDielec.mutateAsync({
        kod: `${dielec.kod}_${op.kod}`,
        nazov_strojovy: op.label ?? op.kod,
        parent_dielec_id: dielec.id,
        operacia: op.kod,
        pocet_ks: 1,
        poradie: 9999,
        x_mm: null,
        y_mm: null,
        z_mm: null,
        material_slot_id: null,
        material_id_direct: null,
        nadmiera_x_mm: "0",
        nadmiera_y_mm: "0",
        nadmiera_aktivna: false,
        smer_vlakien: "0",
        hrana_x1_auto: false,
        hrana_x2_auto: false,
        hrana_y1_auto: false,
        hrana_y2_auto: false,
      });
      toast({
        title: "Operácia pridaná",
        description: `${op.label ?? op.kod} → ${dielec.kod}`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Operáciu sa nepodarilo pridať",
        description: extractErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pridať operáciu — {dielec.kod}</DialogTitle>
          <DialogDescription className="sr-only">
            Pridanie operácie (práce) k tomuto dielcu.
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Hľadať operáciu…"
          aria-label="Hľadať operáciu"
          className="h-8"
        />
        {dostupneKategorie.length > 1 ? (
          <div className="flex flex-wrap gap-1">
            {dostupneKategorie.map((k) => (
              <button
                key={k.kod}
                type="button"
                onClick={() => setKat(kat === k.kod ? null : k.kod)}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[11px] transition-colors",
                  kat === k.kod
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input text-muted-foreground hover:bg-accent",
                )}
              >
                {k.label}
              </button>
            ))}
          </div>
        ) : null}
        <div className="max-h-72 space-y-1 overflow-y-auto text-sm">
          {skupiny.map((skupina) => (
            <div key={skupina.kod ?? "nezaradene"}>
              <p className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {skupina.label}
              </p>
              {skupina.items.map((op) => (
                <button
                  key={op.kod}
                  type="button"
                  disabled={busy}
                  onClick={() => void pick(op)}
                  className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent disabled:opacity-50"
                >
                  <span className="min-w-0 truncate font-medium">
                    {op.label ?? op.kod}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">
                    {formatEur(parseFloat(String(op.cena_eur_per_mj)) || 0)}/{op.mj ?? "?"}
                  </span>
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-muted-foreground">
              Žiadna operácia nevyhovuje hľadaniu.
            </p>
          ) : null}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Množstvo sa počíta z rozmerov dielca (m² = X×Y, m = X) — pri zmene
          rozmerov sa cena prepočíta automaticky.
        </p>
      </DialogContent>
    </Dialog>
  );
}
